// SidebarRecent — the left menu's recent items in pen's list pattern
// ("Projects and Studio", decision 3; "Left menu – detail design" §1, §3, §4),
// pure.
//
// Names only (no colour marker), starting at the list's left edge. A pinned
// section (only when something is pinned, in pin order) above the recent
// list (the chosen sort, checked in View options). Each section's label names
// its list ("Recent projects"): the menu can hold more
// than one list, so a label that only named the sort would not say which. At most five rows per
// section, then "Show all …" when a destination exists. Compact rows are one
// 13 px line; Detailed rows add the item's meta line. A row's "…" menu and
// the section's View options button show on hover and focus. While loading,
// skeleton rows in the real shape.
//
// Props-driven: the list view (pins, sort, density) comes in; every choice
// reports up as a `RecentListCommand`, every item action through the item.

export type { RecentListProps };
export { SidebarRecent, useRowMenu, useSortLabel };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon, type IconName } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Menu, type MenuActionItem, type MenuItem } from "../Menu/Menu.js";
import { RowOverflow } from "../RowOverflow/RowOverflow.js";
import { RowRename } from "../RowOverflow/RowRename.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
import {
    RECENT_SORTS,
    type RecentListCommand,
    type RecentListView,
    type RecentSort,
    recentSectionsOf,
} from "./recentList.js";
import type { RecentItem } from "./sidebarEntries.js";

type RecentListProps = {
    // Which list: the recent items registered with this `list` id.
    readonly id: string;
    // The author's choices for this list (pins, sort, density).
    readonly view: RecentListView;
    readonly onCommand: (cmd: RecentListCommand) => void;
    // What the two sections are called, shown as their labels and read by
    // screen readers ("Pinned projects", "Recent projects").
    readonly labels: { readonly pinned: string; readonly recent: string };
    // "Show all …" under a full section, only when a destination exists.
    readonly showAll?: { readonly label: string; readonly open: () => void };
    // The items are still being read: skeleton rows.
    readonly loading: boolean;
};

const SKELETON_ROWS = 3;

const useSortLabel = (): ((sort: RecentSort) => string) => {
    const { t } = useLingui();
    return (sort) => {
        switch (sort) {
            case "last-opened":
                return t`Last opened`;
            case "last-changed":
                return t`Last changed`;
            case "name":
                return t`Name A–Z`;
            default:
                return exhaustSwitchCase(sort);
        }
    };
};

// The row's "…" menu (one pattern for the left menu and the Projects page):
// only what works for its kind. Pinned rows read Unpin; "Remove from Recent"
// is the left menu's own, for rows not pinned.
const useRowMenu = (
    onCommand: (cmd: RecentListCommand) => void,
    surface: "sidebar" | "page",
): ((item: RecentItem, pinned: boolean) => readonly MenuItem[]) => {
    const { t } = useLingui();
    return (item, pinned) => {
        const action = (
            label: string,
            icon: IconName,
            onSelect: () => void,
        ): MenuActionItem => ({
            _tag: "item",
            label,
            icon: <Icon name={icon} />,
            onSelect,
        });
        const remove: readonly MenuItem[] =
            item.confirmDelete === undefined
                ? []
                : [
                      { _tag: "separator" } satisfies MenuItem,
                      {
                          ...action(t`Delete …`, "trash", item.confirmDelete),
                          tone: "danger",
                      } satisfies MenuItem,
                  ];
        // An archived item: back from the archive, or deleted for good.
        if (item.restore !== undefined)
            return [action(t`Restore`, "undo", item.restore), ...remove];
        return [
            pinned
                ? action(t`Unpin`, "pin", () =>
                      onCommand({ type: "unpin", id: item.id }),
                  )
                : action(t`Pin to top`, "pin", () =>
                      onCommand({ type: "pin", id: item.id }),
                  ),
            ...(item.rename === undefined
                ? []
                : [action(t`Rename`, "pencil", item.rename)]),
            ...(item.duplicate === undefined
                ? []
                : [action(t`Duplicate`, "copy", item.duplicate)]),
            ...(pinned || surface === "page"
                ? []
                : [
                      action(t`Remove from Recent`, "eye-off", () =>
                          onCommand({
                              type: "remove-from-recent",
                              id: item.id,
                          }),
                      ),
                  ]),
            ...(item.archive === undefined
                ? []
                : [action(t`Archive`, "archive", item.archive)]),
            ...remove,
        ];
    };
};

const RecentRow = ({
    item,
    pinned,
    detailed,
    menu,
}: {
    readonly item: RecentItem;
    readonly pinned: boolean;
    readonly detailed: boolean;
    readonly menu: (item: RecentItem, pinned: boolean) => readonly MenuItem[];
}): ReactNode => {
    const { t } = useLingui();
    if (item.renaming !== undefined)
        return (
            <div className="bilda-sidebar__recent-row">
                <RowRename
                    initial={item.title}
                    label={t`New name for ${item.title}`}
                    onSubmit={item.renaming.onSubmit}
                    onCancel={item.renaming.onCancel}
                    className="bilda-sidebar__rename"
                />
            </div>
        );
    const meta = detailed ? item.meta : undefined;
    return (
        <div
            className="bilda-sidebar__recent-row bilda-row-overflow-host"
            data-density={detailed ? "detailed" : "compact"}
        >
            <button
                type="button"
                className="bilda-sidebar__recent-item"
                // The full name, also when the row cuts it.
                title={item.title}
                aria-current={item.current ? "true" : undefined}
                data-active={item.current ? "true" : undefined}
                onClick={item.open}
            >
                <span className="bilda-sidebar__recent-title">
                    {item.title}
                </span>
                {meta === undefined ? null : (
                    <span className="bilda-sidebar__recent-meta">{meta}</span>
                )}
            </button>
            <RowOverflow
                label={t`More options for ${item.title}`}
                items={menu(item, pinned)}
                className="bilda-sidebar__more"
                menuClassName="bilda-sidebar-menu"
            />
        </div>
    );
};

const SidebarRecent = ({
    items,
    list,
}: {
    readonly items: readonly RecentItem[];
    readonly list: RecentListProps;
}): ReactNode => {
    const { t, i18n } = useLingui();
    const sortLabel = useSortLabel();
    const menu = useRowMenu(list.onCommand, "sidebar");
    const { view, onCommand } = list;
    const sections = recentSectionsOf(items, view, i18n.locale);
    const detailed = view.density === "detailed";

    if (list.loading)
        return (
            <section
                className="bilda-sidebar__section"
                aria-label={list.labels.recent}
                aria-busy="true"
            >
                <div className="bilda-sidebar__section-label">
                    <Skeleton width="40%" height="0.5rem" />
                </div>
                {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                    <div
                        // biome-ignore lint/suspicious/noArrayIndexKey: fixed placeholders
                        key={i}
                        className="bilda-sidebar__recent-row"
                        data-density={view.density}
                    >
                        <Skeleton width={`${70 - i * 12}%`} />
                    </div>
                ))}
            </section>
        );

    const viewOptions = (
        <Menu
            align="start"
            contentClassName="bilda-sidebar-menu"
            trigger={
                <IconButton
                    label={t`View options`}
                    variant="ghost"
                    size="sm"
                    // Revealed like a row's "…" (hover, focus, open).
                    className="bilda-row-overflow__trigger bilda-sidebar__view-options"
                >
                    <Icon name="sliders" />
                </IconButton>
            }
            items={[
                { _tag: "label", label: t`Sort by` },
                ...RECENT_SORTS.map(
                    (sort): MenuItem => ({
                        _tag: "item",
                        label: sortLabel(sort),
                        checked: view.sort === sort,
                        onSelect: () => onCommand({ type: "sort-by", sort }),
                    }),
                ),
                { _tag: "separator" },
                { _tag: "label", label: t`View` },
                {
                    _tag: "item",
                    label: t`Compact`,
                    icon: <Icon name="rows-3" />,
                    checked: !detailed,
                    onSelect: () =>
                        onCommand({ type: "show-density", density: "compact" }),
                },
                {
                    _tag: "item",
                    label: t`Detailed`,
                    icon: <Icon name="rows-2" />,
                    checked: detailed,
                    onSelect: () =>
                        onCommand({
                            type: "show-density",
                            density: "detailed",
                        }),
                },
            ]}
        />
    );
    // The View options button sits on the recent label; with everything
    // pinned, on the PINNED label.
    const optionsOnPinned = sections.recent.length === 0;
    const showAll = (more: boolean) =>
        more && list.showAll !== undefined ? (
            <button
                type="button"
                className="bilda-sidebar__show-all"
                onClick={list.showAll.open}
            >
                {list.showAll.label}
            </button>
        ) : null;

    return (
        <>
            {sections.pinned.length > 0 ? (
                <section
                    className="bilda-sidebar__section"
                    aria-label={list.labels.pinned}
                    data-section="pinned"
                >
                    <div className="bilda-sidebar__section-label bilda-row-overflow-host">
                        <h2 className="bilda-sidebar__heading">
                            {list.labels.pinned}
                        </h2>
                        {optionsOnPinned ? viewOptions : null}
                    </div>
                    {sections.pinned.map((item) => (
                        <RecentRow
                            key={item.id}
                            item={item}
                            pinned
                            detailed={detailed}
                            menu={menu}
                        />
                    ))}
                    {showAll(sections.morePinned)}
                </section>
            ) : null}
            {sections.recent.length > 0 ? (
                <section
                    className="bilda-sidebar__section"
                    aria-label={list.labels.recent}
                    data-section="recent"
                >
                    <div className="bilda-sidebar__section-label bilda-row-overflow-host">
                        <h2
                            className="bilda-sidebar__heading"
                            title={sortLabel(view.sort)}
                        >
                            {list.labels.recent}
                        </h2>
                        {viewOptions}
                    </div>
                    {sections.recent.map((item) => (
                        <RecentRow
                            key={item.id}
                            item={item}
                            pinned={false}
                            detailed={detailed}
                            menu={menu}
                        />
                    ))}
                    {showAll(sections.moreRecent)}
                </section>
            ) : null}
        </>
    );
};
